import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, XCircle, MoreVertical } from 'lucide-react';
import useAccessibility from '../hooks/useAccessibility';

export const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { accessibility, toggleColorblind, toggleHighContrast, toggleLargeText, resetAccessibility } = useAccessibility();

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        title="Paramètres d'accessibilité"
        aria-label="Options d'accessibilité"
      >
        <Eye size={20} style={{ color: '#f5f5f7' }} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-72 bg-[#1a1a1a] border border-blue-500/30 rounded-2xl p-6 z-50 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white text-lg" style={{ letterSpacing: '-0.02em' }}>
                Accessibilité
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:opacity-70 transition-opacity"
              >
                <XCircle size={18} style={{ color: '#a1a1a6' }} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Colorblind Modes */}
              <div>
                <p className="text-sm font-semibold text-white mb-2">Mode Daltonien</p>
                <div className="space-y-2">
                  {[
                    { key: 'protanopia', label: 'Protanopie (Rouge)' },
                    { key: 'deuteranopia', label: 'Deutéranopie (Vert)' },
                    { key: 'tritanopia', label: 'Tritanopie (Bleu-Jaune)' },
                  ].map((mode) => (
                    <button
                      key={mode.key}
                      onClick={() => toggleColorblind(mode.key)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        accessibility.colorblind === mode.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* High Contrast Toggle */}
              <div>
                <button
                  onClick={toggleHighContrast}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    accessibility.highContrast
                      ? 'bg-yellow-600 text-black'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {accessibility.highContrast ? '✓ Contraste Élevé' : 'Contraste Élevé'}
                </button>
              </div>

              {/* Large Text Toggle */}
              <div>
                <button
                  onClick={toggleLargeText}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    accessibility.largeText
                      ? 'bg-cyan-600 text-white'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {accessibility.largeText ? '✓ Texte Agrandi' : 'Texte Agrandi'}
                </button>
              </div>

              {/* Reset Button */}
              <button
                onClick={() => {
                  resetAccessibility();
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-300 hover:bg-white/10 transition-all border border-white/10"
              >
                Réinitialiser
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 leading-relaxed">
              Ces paramètres sont enregistrés localement sur votre appareil.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccessibilityPanel;
